import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import styled from 'styled-components';

import amplitude from 'amplitude-js';
import { toggleNavBar } from 'apps/auth/actions/appAction';
import { updateExtraByName } from 'apps/auth/actions/extraAction';
import { fetchContentCards } from 'apps/auth/analytics/braze';
import WSChat from 'apps/auth/components/chat/WSChat';
import { Button } from 'apps/auth/components/common/Button/Button';
import { DiskButton } from 'apps/auth/components/common/DiskButton/DiskButton';
import BottomMenu from 'apps/auth/components/common/Layout/BottomMenu/BottomMenu';
import { MainColumn } from 'apps/auth/components/common/Layout/MainColumn/MainColumn';
import MenuSideBar from 'apps/auth/components/common/Layout/MenuSideBar/MenuSideBar';
import { NavBar } from 'apps/auth/components/common/Layout/NavBar/NavBar';
import { StubPage } from 'apps/auth/components/common/Layout/StubPage';
import Popup from 'apps/auth/components/common/Popup/Popup';
import { SnackBar } from 'apps/auth/components/common/SnackBar/SnackBar';
import { Text } from 'apps/auth/components/common/Text/Text';
import Notification from 'apps/auth/components/history/Notification';
import WelcomePage from 'apps/auth/components/history/send/WelcomePage';
import BasePage from 'apps/auth/components/utils/BasePage';
import MessageBodyService from 'apps/auth/components/utils/MessageBodyService';
import ModalNew from 'apps/auth/components/utils/ModalNew';
import ModalWithTimer from 'apps/auth/components/utils/ModalWithTimer';
import Spinner from 'apps/auth/components/utils/Spinner/Spinner';
import { INVITE_FRIENDS_CLICKED } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { BUTTON_VARIANT } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { EARN_BONUS, GIFT } from 'apps/auth/constants/amplitude/AmplitudeParamValues';
import { JWT, SEND, USER_COUNTRY_NAME } from 'apps/auth/constants/Constants';
import { AUTH_PROFILE, AUTH_PROFILE_INVITE } from 'apps/auth/constants/UrlConstants';
import { resolveArguments } from 'apps/auth/helpers/utils';
import { useApp, useAuth, useExtra, useLanguage, usePageMessages } from 'apps/auth/hooks/selectors';
import { getMaxPossibleAmount } from 'apps/auth/services/bonusService';
import media from 'apps/auth/theme/media';
import Cookies from 'universal-cookie';

import AppInboxModal from '../appInbox/AppInboxModal';

const MainContent = styled.div`
    min-height: 100vh;
    position: relative;
    background: ${(props) => (props.isNewMainScreen ? props.theme.grey_8_lc : null)};

    ${media.desktop1224} {
        padding-left: ${(props) => (props.isNewMainScreen ? null : '360px')};
    }
`;

const NotificationContainer = styled.div`
    width: 100%;
    margin: 0 16px;
    pointer-events: auto;
`;

const NotificationButtonWrapper = styled.div`
    position: relative;

    &::before {
        content: '';
        position: absolute;
        top: 6px;
        right: 8px;
        width: 8px;
        height: 8px;
        background-color: ${(props) => props.theme.pink_100};
        border-radius: 50%;
        border: 1px solid ${(props) => props.theme.white};
        display: ${(props) => (props.hasUnread ? null : 'none')};
    }
`;

const ProfileButtonWrapper = styled.div`
    margin-left: 16px;

    ${media.desktop1224} {
        display: ${(props) => (props.isHiddenDesktop ? 'none' : 'block')};
    }
`;

const FlexWrap = styled.div`
    display: flex;
`;

const cookies = new Cookies();

export default function BasePageWithLeftMenu(
    PageComponent,
    currentPage = SEND,
    options = {
        showChat: true,
        canShowConfirmNotificationBlock: true,
    },
) {
    const ComposedComponent = (props) => {
        const { hasInboxMessages, showNewMainScreen, mgmShortcutType, bonusReferralInfo } = useExtra();
        const { ballId, jwt, userCountryName } = useAuth();
        const { country } = useApp();
        const { lang } = useLanguage();
        const { mgmABTestButtonTitle } = usePageMessages();

        const modalRef = useRef(null);

        const [showWelcomePage, toggleWelcomePage] = useState(props.showWelcomePage);
        const [contentCards, setContentCards] = useState([]);
        const [hasUnread, setHasUnread] = useState(false);

        useEffect(() => {
            if (currentPage === SEND) {
                return fetchContentCards(ballId, (cards) => {
                    setContentCards(cards);
                    setHasUnread(cards.some((card) => !card.viewed && !card.pinned));
                });
            }
        }, [ballId, currentPage]);

        useEffect(() => {
            props.toggleNavBar(false);
            cookies.set(JWT, jwt, { path: '/' });
            cookies.set(USER_COUNTRY_NAME, userCountryName, { path: '/' });
        }, [jwt, userCountryName]);

        useEffect(() => {
            toggleWelcomePage(props.showWelcomePage);
        }, [props.showWelcomePage]);

        const getRenderComponent = () => {
            if (props.gaServerNotAvailable && props?.stateOptions?.isGA) {
                return (
                    <MainColumn>
                        <StubPage />
                    </MainColumn>
                );
            } else if (!props.loading) {
                return (
                    <>
                        <PageComponent {...props} data={props.data} pageMessages={props.pageMessages} loading={props.loading} />
                        {props.children}
                    </>
                );
            } else {
                return false;
            }
        };

        if (showWelcomePage) {
            return <WelcomePage hideWelcomePage={() => props.updateExtraByName('welcomePage', false).then(() => toggleWelcomePage(false))} />;
        } else {
            return (
                <>
                    <SnackBar />
                    <MainContent isNewMainScreen={showNewMainScreen}>
                        <NavBar
                            fullwidth={!showNewMainScreen}
                            align={showNewMainScreen || mgmShortcutType !== '' ? 'space-between' : 'right'}
                            isNewMainScreen={showNewMainScreen}
                        >
                            {bonusReferralInfo && mgmShortcutType === 'button' && (
                                <Button
                                    type={'button'}
                                    variant={'primary'}
                                    size={'small'}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        amplitude.getInstance().logEvent(INVITE_FRIENDS_CLICKED, { [BUTTON_VARIANT]: EARN_BONUS });
                                        props.toggleSpinner(true);
                                        window.location.href = AUTH_PROFILE_INVITE;
                                    }}
                                >
                                    {resolveArguments(mgmABTestButtonTitle, [getMaxPossibleAmount(lang, country, bonusReferralInfo)])}{' '}
                                    <Text color={'white'} size={'secondary'} smallCaps>
                                        {bonusReferralInfo?.referralInfo?.bonus?.ccyIso?.toLowerCase()}
                                    </Text>
                                </Button>
                            )}
                            {bonusReferralInfo && mgmShortcutType === 'gift' && (
                                <DiskButton
                                    type={'button'}
                                    variant={'secondary'}
                                    iconName="gift"
                                    iconColor={'primary'}
                                    marginRight={'16px'}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        amplitude.getInstance().logEvent(INVITE_FRIENDS_CLICKED, { [BUTTON_VARIANT]: GIFT });
                                        props.toggleSpinner(true);
                                        window.location.href = AUTH_PROFILE_INVITE;
                                    }}
                                />
                            )}
                            <FlexWrap>
                                {currentPage === SEND && hasInboxMessages && (
                                    <NotificationButtonWrapper hasUnread={hasUnread}>
                                        <DiskButton
                                            type={'button'}
                                            iconName="bell"
                                            onClick={() => modalRef.current?.open(<AppInboxModal modalRef={modalRef} cards={contentCards} />)}
                                        />
                                    </NotificationButtonWrapper>
                                )}
                                <ProfileButtonWrapper isHiddenDesktop={!showNewMainScreen}>
                                    <DiskButton to={AUTH_PROFILE} iconName="person" />
                                </ProfileButtonWrapper>
                            </FlexWrap>
                            {showNewMainScreen && (
                                <>
                                    <NotificationContainer>
                                        <Notification isNewMainScreen />
                                    </NotificationContainer>
                                    <DiskButton iconName="plus-minus" />
                                </>
                            )}
                        </NavBar>
                        {getRenderComponent()}
                    </MainContent>
                    {showNewMainScreen ? <BottomMenu currentPage={currentPage} /> : <MenuSideBar currentPage={currentPage} isGA={props.stateOptions.isGA} />}
                    {props.isShowModal && props.modalComponent}
                    {props.isShowModalWithTimer && <ModalWithTimer />}
                    <Popup />
                    <MessageBodyService />
                    <Spinner />
                    {options.showChat && <WSChat />}
                    <ModalNew ref={modalRef} />
                </>
            );
        }
    };

    const mapStateToProps = ({ auth, app, extra }) => {
        return {
            canConfirmEmail: auth.canConfirmEmail,
            modalComponent: app.modalComponent,
            isShowModalWithTimer: app.isShowModalWithTimer,
            isShowModal: app.isShowModal,
            showWelcomePage: extra.welcomePage,
            gaServerNotAvailable: extra.gaServerNotAvailable,
            showSpecialWelcomeScreen: extra.showSpecialWelcomeScreen,
        };
    };
    return connect(mapStateToProps, { toggleNavBar, updateExtraByName })(BasePage(ComposedComponent));
}
